import { createContext, useContext, useState, useEffect, type ReactNode, type FC } from 'react';
import {
  hierarchicalOrganizations,
  defaultOrganization,
  defaultMode,
  type Organization as HierarchicalOrg,
  type Portfolio as HierarchicalPortfolio,
  type Mode as HierarchicalMode,
  type TenantMeta,
} from '@shared/hierarchicalData';
import { organizationAPI } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import type { ExtendedUser } from '@/lib/oidcConfig';
import { canonicalizeRoles } from '@/lib/roles';

export type Mode = HierarchicalMode;

export interface Organization extends HierarchicalOrg {}
export interface Portfolio extends HierarchicalPortfolio {}
export interface Tenant extends TenantMeta {}

interface ScopeState {
  organization: Organization | null;
  tenant: Tenant | null;
  portfolio: Portfolio | null;
  mode: Mode | null;
}

interface ScopeContextType extends ScopeState {
  organizations: Organization[];
  setOrganization: (org: Organization) => void;
  setPortfolio: (portfolio: Portfolio) => void;
  isRugiraStaff: boolean;
  scopeReady: boolean;
}

const ScopeContext = createContext<ScopeContextType | null>(null);

const STORAGE_KEY = 'rugira_scope';
const ADMIN_SCOPE_ROLES = new Set([
  'org_owner',
  'rugira_platform_admin',
  'rugira_compliance_admin',
  'rugira_support',
]);

interface ScopeProviderProps {
  children: ReactNode;
}

const titleizeScopeLabel = (value?: string | null): string => {
  const normalized = value?.trim();
  if (!normalized) return 'Current';
  if (normalized.toLowerCase() === 'demo') return 'demo';
  return normalized
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const rolesForScope = (user: ExtendedUser | null, currentTenant: string | null): string[] => {
  if (!user) return [];
  if (currentTenant && user.tenant_roles?.[currentTenant]) {
    return canonicalizeRoles(user.tenant_roles[currentTenant]);
  }
  if (Array.isArray(user.roles)) {
    return canonicalizeRoles(user.roles);
  }
  return [];
};

const canLoadAdminScope = (user: ExtendedUser | null, currentTenant: string | null): boolean =>
  rolesForScope(user, currentTenant).some((role) => ADMIN_SCOPE_ROLES.has(role));

const buildTenantFallbackScope = (
  user: ExtendedUser | null,
  currentTenant: string | null,
  currentTenantUuid: string | null,
): ScopeState => {
  const tenantSlug = currentTenant || user?.current_tenant || user?.tenant_id || 'current';
  const tenantLabel = titleizeScopeLabel(tenantSlug);
  const tenant: Tenant = {
    id: currentTenantUuid || user?.current_tenant_uuid || user?.tenant_uuid || tenantSlug,
    name: tenantLabel,
  };
  const modeFallback: Mode = defaultMode ?? { id: 'paper', name: 'Paper', enabled: true };
  const desk: Portfolio = {
    id: '',
    name: 'Desk',
    tenant,
    modes: [modeFallback],
    hasLiveEnabled: false,
  };
  const org: Organization = {
    id: '',
    name: `${tenantLabel} Organization`,
    status: 'active',
    portfolios: [desk],
  };

  return {
    organization: org,
    tenant,
    portfolio: desk,
    mode: modeFallback,
  };
};

export const ScopeProvider: FC<ScopeProviderProps> = ({ children }: ScopeProviderProps) => {
  const [scopeReady, setScopeReady] = useState(false);
  const demoDataEnabled = import.meta.env.VITE_DEMO_MODE === '1';
  const { isAuthenticated, user, currentTenant, currentTenantUuid } = useAuth();

  // Initialize with defaults from hierarchical data
  const [organization, setOrganizationState] = useState<Organization | null>(null);
  const [tenant, setTenantState] = useState<Tenant | null>(null);
  const [portfolio, setPortfolioState] = useState<Portfolio | null>(null);
  const [mode, setModeState] = useState<Mode | null>(null);

  // Check if user is Rugira staff (feature flag)
  const isRugiraStaff = sessionStorage.getItem('rugira_staff') === 'true';

  // Load scope from URL or storage on mount, and reload when auth state changes
  useEffect(() => {
    if (!demoDataEnabled) {
      // In non-demo mode (testnet/prod), fetch organizations and portfolios from API
      // Only attempt if user is authenticated to avoid unnecessary failed requests
      const loadFromAPI = async () => {
        if (!isAuthenticated) {
          // Not authenticated yet, skip API loading and mark ready
          // The scope will be loaded later when auth completes (effect re-runs)
          setScopeReady(true);
          return;
        }

        if (!canLoadAdminScope(user, currentTenant)) {
          const fallback = buildTenantFallbackScope(user, currentTenant, currentTenantUuid);
          setOrganizationState(fallback.organization);
          setTenantState(fallback.tenant);
          setPortfolioState(fallback.portfolio);
          setModeState(fallback.mode);
          setScopeReady(true);
          return;
        }

        try {
          const orgs = await organizationAPI.list();
          if (orgs.length > 0) {
            const firstOrg = orgs[0];
            const portfolios = await organizationAPI.getPortfolios(firstOrg.id);

            // Create organization object compatible with the context
            const org: Organization = {
              id: firstOrg.id,
              name: firstOrg.name,
              status: firstOrg.status || 'active',
              portfolios: portfolios.map(p => ({
                id: p.portfolio_id,
                name: p.name,
                tenant: { id: 'default', name: 'Default' },
                modes: [{
                  id: p.default_mode || 'paper',
                  name: p.default_mode === 'live' ? 'Live' : 'Paper',
                  enabled: true,
                }],
                hasLiveEnabled: p.live_enabled,
              })),
            };

            setOrganizationState(org);
            if (org.portfolios && org.portfolios.length > 0) {
              const defaultPortfolio = org.portfolios[0];
              setPortfolioState(defaultPortfolio);
              setTenantState(defaultPortfolio.tenant ?? null);
              setModeState(defaultPortfolio.modes[0] ?? null);
            }
          }
        } catch (error) {
          console.warn('Failed to load scope from API, using defaults', error);
        }
        setScopeReady(true);
      };

      loadFromAPI();
      return;
    }

    const loadScope = () => {
      let loadedOrg: Organization | null = null;
      let loadedPortfolio: Portfolio | null = null;
      let loadedMode: Mode | null = null;

      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        try {
          const parsed = JSON.parse(stored);

          const findPortfolioById = (portfolioId?: string): { org: Organization; portfolio: Portfolio } | null => {
            if (!portfolioId) return null;
            for (const org of hierarchicalOrganizations) {
              const candidate = org.portfolios?.find((p) => p.id === portfolioId);
              if (candidate) {
                return { org, portfolio: candidate };
              }
            }
            return null;
          };

          const findPortfolioByTenantId = (tenantId?: string): { org: Organization; portfolio: Portfolio } | null => {
            if (!tenantId) return null;
            for (const org of hierarchicalOrganizations) {
              const candidate = org.portfolios?.find((p) => p.tenant?.id === tenantId);
              if (candidate) {
                return { org, portfolio: candidate };
              }
            }
            return null;
          };

          const byPortfolio = findPortfolioById(parsed.portfolioId);
          const byTenant = findPortfolioByTenantId(parsed.tenantId);

          if (byPortfolio) {
            loadedOrg = byPortfolio.org;
            loadedPortfolio = byPortfolio.portfolio;
          } else if (byTenant) {
            loadedOrg = byTenant.org;
            loadedPortfolio = byTenant.portfolio;
          } else if (parsed.organizationId) {
            loadedOrg = hierarchicalOrganizations.find((o) => o.id === parsed.organizationId) || null;
          }

          if (loadedPortfolio) {
            loadedMode = loadedPortfolio.modes.find((m) => m.id === parsed.modeId) || null;
          }
        } catch (e) {
          console.error('Failed to parse stored scope');
        }
      }

      if (!loadedOrg) {
        loadedOrg = defaultOrganization ?? null;
      }
      if (loadedOrg && !loadedPortfolio) {
        loadedPortfolio = loadedOrg.portfolios?.[0] ?? null;
      }
      if (loadedPortfolio && !loadedMode) {
        loadedMode = loadedPortfolio.modes[0] ?? null;
      }
      if (!loadedMode) {
        loadedMode = defaultMode ?? null;
      }

      setOrganizationState(loadedOrg);
      setPortfolioState(loadedPortfolio);
      setTenantState(loadedPortfolio?.tenant ?? null);
      setModeState(loadedMode);
      setScopeReady(true);
    };

    loadScope();
  }, [isAuthenticated, demoDataEnabled, user, currentTenant, currentTenantUuid]);

  // Persist scope changes
  const persistScope = () => {
    if (organization && portfolio && mode) {
      const scopeData: Record<string, string> = {
        organizationId: organization.id,
        portfolioId: portfolio.id,
        modeId: mode.id,
      };
      if (portfolio.tenant?.id) {
        scopeData.tenantId = portfolio.tenant.id;
      }
      if (demoDataEnabled) {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(scopeData));
      }
    }
  };

  useEffect(() => {
    if (scopeReady && organization && portfolio && mode) {
      persistScope();
    }
  }, [organization, portfolio, mode, scopeReady]);

  const setOrganization = (org: Organization) => {
    if (!demoDataEnabled) {
      return;
    }
    setOrganizationState(org);
    const firstPortfolio = org.portfolios?.[0] ?? null;
    setPortfolioState(firstPortfolio);
    setTenantState(firstPortfolio?.tenant ?? null);
    if (firstPortfolio) {
      setModeState(firstPortfolio.modes[0]);
    }

    // Emit audit event
    console.log('Audit event: scope.organization.changed', { from: organization?.id, to: org.id });
  };

  const setPortfolio = (newPortfolio: Portfolio) => {
    if (!demoDataEnabled) {
      return;
    }
    setPortfolioState(newPortfolio);
    setTenantState(newPortfolio.tenant ?? null);
    // Reset to first mode in the portfolio
    setModeState(newPortfolio.modes[0]);

    // Emit audit event
    console.log('Audit event: scope.portfolio.changed', { from: portfolio?.id, to: newPortfolio.id });
  };

  const contextValue: ScopeContextType = {
    organization,
    tenant,
    portfolio,
    mode,
    organizations: demoDataEnabled ? hierarchicalOrganizations : organization ? [organization] : [],
    setOrganization,
    setPortfolio,
    isRugiraStaff,
    scopeReady,
  };

  return (
    <ScopeContext.Provider value={contextValue}>
      {children}
    </ScopeContext.Provider>
  );
};

export const useScope = (): ScopeContextType => {
  const context = useContext(ScopeContext);
  if (!context) {
    throw new Error('useScope must be used within ScopeProvider');
  }
  return context;
};
